import { z } from "zod/v4";

import {
  BaseCheckoutBlockDtoDescriptions,
  TrustBadgeCheckoutBlockDtoDescriptions,
  UpsellCheckoutBlockDtoDescriptions,
} from "../descriptions/checkout-block-dto.describe";
import { assertDescriptions } from "../descriptions/types";

import { CartTargetingSchema } from "./cart-targeting";
import {
  UpsellCompareAtPriceDisplayList,
  UpsellDiscountSchema,
  UpsellDiscountTypeList,
  UpsellProductItemSchema,
  UpsellProductRulesSchema,
} from "./upsell-product-rules";

export const TypographySettingsSchema = z.object({
  fontSize: z.string(),
  fontStyle: z.string(),
});

const BaseTypographySchema = z.object({
  header: TypographySettingsSchema,
});

const BaseBackgroundColorsSchema = z.object({
  header: z.string(),
  item: z.string(),
  block: z.string(),
});

const BaseColorsSchema = z.object({
  headerText: z.string(),
});

const BaseLayoutSchema = z.object({
  headerTextAlign: z.string(),
  bodyAlign: z.string(),
  itemTextAlign: z.string(),
  itemPositionAlign: z.string(), // left / right / top / bottom
});

const TrustBadgeLayoutSchema = BaseLayoutSchema.extend({
  rowCount: z.number().int(),
  columnCount: z.number().int(),
});

const UpsellLayoutSchema = BaseLayoutSchema.extend({
  mobile: z.object({
    rowCount: z.number().int(),
    columnCount: z.number().int(),
  }),
  desktop: z.object({
    rowCount: z.number().int(),
    columnCount: z.number().int(),
  }),
});

const BaseAppearanceSchema = z.object({
  headerBorderThickness: z.string(),
  headerBorderStyle: z.string().default("none"),
  headerCornerRadius: z.string(),
  headerPadding: z.string(),
  bodySpacing: z.string(),
  headingToBodySpacing: z.string(),
  blockBorderThickness: z.string(),
  blockBorderStyle: z.string().default("none"),
  blockPadding: z.string(),
  blockCornerRadius: z.string(),
});

const UpsellTypographySchema = BaseTypographySchema.extend({
  productTitle: TypographySettingsSchema,
  variantTitle: TypographySettingsSchema.default({
    fontSize: "base",
    fontStyle: "normal",
  }), //todo orphaned/inaccessible in ui
  price: TypographySettingsSchema,
  button: TypographySettingsSchema,
});

const UpsellBackgroundColorsSchema = BaseBackgroundColorsSchema.extend({
  button: z.string().default("auto"),
  productUnit: z.string(),
});

const UpsellColorsSchema = BaseColorsSchema.extend({
  productText: z.string(),
  variantText: z.string().default("subdued"),
  priceText: z.string(),
  carouselArrowColor: z.string().default("auto"),
  carouselArrowStyle: z.string().default("primary"),
});

const UpsellAppearanceSchema = BaseAppearanceSchema.extend({
  productBorderThickness: z.string(),
  productBorderStyle: z.string().default("none"),
  productCornerRadius: z.string(),
  productTextSpacing: z.string(),
});

export const UpsellBlockStylesSchema = z.object({
  typography: UpsellTypographySchema,
  backgroundColors: UpsellBackgroundColorsSchema,
  colors: UpsellColorsSchema,
  layout: UpsellLayoutSchema,
  appearance: UpsellAppearanceSchema,
});

const TrustBadgeTypographySchema = BaseTypographySchema.extend({
  itemTitle: TypographySettingsSchema,
  itemSubtitle: TypographySettingsSchema,
});

const TrustBadgeColorsSchema = BaseColorsSchema.extend({
  itemTitleText: z.string(),
  itemSubtitleText: z.string(),
});

const TrustBadgeAppearanceSchema = BaseAppearanceSchema.extend({
  itemBorderThickness: z.string(),
  itemBorderStyle: z.string().default("none"),
  itemCornerRadius: z.string(),
  itemTextSpacing: z.string(),
});

export const TrustBadgeBlockStylesSchema = z.object({
  typography: TrustBadgeTypographySchema,
  backgroundColors: BaseBackgroundColorsSchema,
  colors: TrustBadgeColorsSchema,
  layout: TrustBadgeLayoutSchema,
  appearance: TrustBadgeAppearanceSchema,
});

const TimerTypographySchema = z.object({
  text: TypographySettingsSchema,
});

export const TimerSeparatorList = ["colon", "dot", "units"] as const;
export type TimerSeparator = (typeof TimerSeparatorList)[number];

export const TimerDigitSizeList = ["small", "base"] as const;
export type TimerDigitSize = (typeof TimerDigitSizeList)[number];

const TimerDigitsSchema = z.object({
  separator: z.enum(TimerSeparatorList).default("colon"),
  size: z.enum(TimerDigitSizeList).default("base"),
});

const TimerBackgroundColorsSchema = z.object({
  block: z.string(),
  expired: z.string(),
});

const TimerColorsSchema = z.object({
  text: z.string(),
  expiredText: z.string(),
  digit: z.string().default("info"),
});

const TimerLayoutSchema = z.object({
  textAlign: z.string(),
});

const TimerAppearanceSchema = z.object({
  blockBorderStyle: z.string().default("none"),
  blockBorderThickness: z.string(),
  blockCornerRadius: z.string(),
  blockPadding: z.string(),
});

export const TimerBlockStylesSchema = z.object({
  typography: TimerTypographySchema,
  backgroundColors: TimerBackgroundColorsSchema,
  colors: TimerColorsSchema,
  layout: TimerLayoutSchema,
  appearance: TimerAppearanceSchema,
  digits: TimerDigitsSchema.default({ separator: "colon", size: "base" }),
});

// --- Shipping progress bar ---------------------------------------------------

export const ShippingProgressBarTemplateList = [
  "minimal",
  "milestones",
] as const;
export type ShippingProgressBarTemplate =
  (typeof ShippingProgressBarTemplateList)[number];

const ShippingProgressBarTypographySchema = BaseTypographySchema.extend({
  // Typography for the progress message / tier labels.
  label: TypographySettingsSchema,
});

const ShippingProgressBarColorsSchema = BaseColorsSchema.extend({
  // Appearance of the progress message / tier labels.
  labelText: z.string(),
  // Semantic tone of the `<s-progress>` fill (ToneKeyword, e.g. "accent").
  // The track color is managed by the component itself.
  barTone: z.string(),
});

const ShippingProgressBarBackgroundColorsSchema =
  BaseBackgroundColorsSchema.extend({
    // `milestones`-only icon badge background (CheckoutBlockBackgroundColor).
    // Defaulted so blocks persisted before this field stay valid on parse.
    milestoneIcon: z.string().default("subdued"),
  });

const ShippingProgressBarAppearanceSchema = BaseAppearanceSchema.extend({
  // `milestones`-only icon badge styling. CheckoutBlockCornerRadius /
  // CheckoutBlockIconSize / CheckoutBlockPaddingSpacing values respectively.
  // Defaulted so blocks persisted before these fields stay valid on parse.
  milestoneIconCornerRadius: z.string().default("fullyRounded"),
  milestoneIconSize: z.string().default("base"),
  milestoneIconPadding: z.string().default("tight"),
});

const ShippingProgressBarLayoutSchema = BaseLayoutSchema.extend({
  // Alignment of the single under-bar progress message (start / center / end).
  // Defaulted so blocks persisted before this field stay valid on parse.
  messageTextAlign: z.string().default("center"),
});

export const ShippingProgressBarBlockStylesSchema = z.object({
  typography: ShippingProgressBarTypographySchema,
  backgroundColors: ShippingProgressBarBackgroundColorsSchema,
  colors: ShippingProgressBarColorsSchema,
  layout: ShippingProgressBarLayoutSchema,
  appearance: ShippingProgressBarAppearanceSchema,
});

export type TrustBadgeBlockStyles = z.infer<typeof TrustBadgeBlockStylesSchema>;
export type UpsellBlockStyles = z.infer<typeof UpsellBlockStylesSchema>;
export type TimerBlockStyles = z.infer<typeof TimerBlockStylesSchema>;
export type ShippingProgressBarBlockStyles = z.infer<
  typeof ShippingProgressBarBlockStylesSchema
>;

export type CheckoutBlockStyles =
  | TrustBadgeBlockStyles
  | UpsellBlockStyles
  | TimerBlockStyles
  | ShippingProgressBarBlockStyles;

const ImageSizeUnitSchema = z.preprocess(
  (value) => value ?? "pixels",
  z.enum(["pixels", "percentage"])
);

export const TrustBadgeVariableItemSchema = z.object({
  order: z.number(),
  title: z.string().nullish(),
  subtitle: z.string().nullish(),
  type: z.string(), // icon / image / none
  icon: z.string().nullish(),
  iconColor: z.string().nullish(),
  iconSize: z.string().nullish(),
  imageSize: z.number().min(1).max(500).nullable(),
  imageSizeUnit: ImageSizeUnitSchema,
  imageAspectRatio: z.string().nullish(),
  image: z.string().nullish(),
  mediaSpacing: z.string(),
});

export type TrustBadgeVariableItem = z.infer<
  typeof TrustBadgeVariableItemSchema
>;

const BaseCheckoutBlockVariablesSchema = z.object({
  headerText: z.string(),
});

export const UpsellCheckoutBlockVariablesSchema =
  BaseCheckoutBlockVariablesSchema.extend({
    maxProductLimit: z.number().int().min(1).max(10).default(3),
    excludeItemsInCart: z.boolean().default(false),
    excludeOutOfStock: z.boolean().default(false),
    buttonText: z.string(),
    imageSize: z.number().min(1).max(500),
    imageSizeUnit: ImageSizeUnitSchema,
    imageAspectRatio: z.string(),
    mediaSpacing: z.string(),
    // The block's own pricing, and the ONLY place a rule-bound block's
    // discount comes from. A rule decides which products are recommended; it
    // never prices them. Mirrors `OnsiteUpsellConfigSchema.discount` so all
    // three upsell surfaces carry the same shape. Legacy blocks still read it
    // from `upsellproductrules.action` and ignore this.
    discount: UpsellDiscountSchema.optional(),
  });

const TrustBadgeCheckoutBlockVariablesSchema =
  BaseCheckoutBlockVariablesSchema.extend({
    items: z.array(TrustBadgeVariableItemSchema),
  });

export type TrustBadgeBlockVariables = z.infer<
  typeof TrustBadgeCheckoutBlockVariablesSchema
>;
export type UpsellBlockVariables = z.infer<
  typeof UpsellCheckoutBlockVariablesSchema
>;

export const TimerModeList = ["fixedDate", "perVisitor", "recurring"] as const;
export type TimerMode = (typeof TimerModeList)[number];

export const TimerResetIntervalUnitList = ["hour", "day", "week"] as const;
export type TimerResetIntervalUnit =
  (typeof TimerResetIntervalUnitList)[number];

// ISO weekday (1=Mon..7=Sun), matching Luxon's own WeekdayNumbers convention.
export const TimerResetDayOfWeekList = [1, 2, 3, 4, 5, 6, 7] as const;
export type TimerResetDayOfWeek = (typeof TimerResetDayOfWeekList)[number];

export const TimerExpiredBehaviorList = ["remove", "showText"] as const;
export type TimerExpiredBehavior = (typeof TimerExpiredBehaviorList)[number];

export const TimerBlockTemplateList = [
  "textCountdown",
  "digitalClock",
  "inlinePill",
] as const;
export type TimerBlockTemplateType = (typeof TimerBlockTemplateList)[number];

const TIME_OF_DAY_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const TimerCheckoutBlockVariablesSchema = z
  .object({
    timerMode: z.enum(TimerModeList).default("fixedDate"),
    endDateTime: z.string().nullish(), // ISO-8601 instant (fixedDate mode)
    durationMinutes: z.number().int().min(1).default(15), // perVisitor mode
    // --- recurring mode ---
    resetIntervalUnit: z.enum(TimerResetIntervalUnitList).nullish(),
    resetTimeOfDay: z.string().regex(TIME_OF_DAY_REGEX).nullish(), // "HH:mm", 24h, day/week only
    // ISO weekday (1=Mon..7=Sun), week only. A literal union (not a plain
    // ranged number) so the type is exact everywhere it flows, no `as` casts
    // needed downstream.
    resetDayOfWeek: z
      .union(TimerResetDayOfWeekList.map((d) => z.literal(d)))
      .nullish(),
    resetMinuteOfHour: z.number().int().min(0).max(59).nullish(), // hour only
    // IANA id resolved from the org's Shopify timezone at edit time (the
    // extension has no runtime access to it), not user-editable.
    resetTimezone: z.string().nullish(),
    countdownText: z.string(), // may contain {{Time remaining}} / {{End date}}
    expiredBehavior: z.enum(TimerExpiredBehaviorList).default("showText"),
    expiredText: z.string(),
    timerTemplate: z.enum(TimerBlockTemplateList).default("textCountdown"),
    headline: z.string().default(""), // digitalClock / inlinePill caption
    showDays: z.boolean().default(false), // digitalClock units shown
    showHours: z.boolean().default(true),
    showMinutes: z.boolean().default(true),
    showSeconds: z.boolean().default(true),
  })
  // A fixedDate timer with no parseable end date can never render at checkout,
  // so reject it at the boundary rather than letting it save as an invisible
  // block (perVisitor timers don't use endDateTime).
  .refine(
    (v) =>
      v.timerMode !== "fixedDate" ||
      (typeof v.endDateTime === "string" &&
        !Number.isNaN(Date.parse(v.endDateTime))),
    {
      path: ["endDateTime"],
      message: "A fixed-date timer needs a valid end date and time.",
    }
  )
  // A recurring timer needs a resolved timezone plus whichever fields its
  // interval unit depends on, or it can never resolve a next reset instant.
  .refine(
    (v) =>
      v.timerMode !== "recurring" ||
      (typeof v.resetTimezone === "string" &&
        v.resetTimezone.length > 0 &&
        typeof v.resetIntervalUnit === "string" &&
        (v.resetIntervalUnit !== "hour" ||
          (typeof v.resetMinuteOfHour === "number" &&
            v.resetMinuteOfHour >= 0 &&
            v.resetMinuteOfHour <= 59)) &&
        (v.resetIntervalUnit === "hour" ||
          (typeof v.resetTimeOfDay === "string" &&
            TIME_OF_DAY_REGEX.test(v.resetTimeOfDay))) &&
        // The 1-7 range is already enforced by resetDayOfWeek's own literal
        // union above; this only needs to check it was set at all.
        (v.resetIntervalUnit !== "week" ||
          typeof v.resetDayOfWeek === "number")),
    {
      path: ["resetTimeOfDay"],
      message:
        "A recurring timer needs a valid reset time, day (if weekly), and timezone.",
    }
  );

export type TimerBlockVariables = z.infer<
  typeof TimerCheckoutBlockVariablesSchema
>;

export const ShippingProgressTierSchema = z.object({
  // Value expressed in `currency`; the threshold a buyer must reach.
  threshold: z.number().positive(),
  // Copy shown while the tier is unmet; supports the {{amount_remaining}} token.
  // In the `minimal` template only the last tier's `message` is rendered.
  message: z.string(),
  // Copy shown once this tier's threshold is reached (per-tier, not global).
  messageComplete: z.string(),
  // --- `milestones`-only per-breakpoint marker icons (ignored by `minimal`) ---
  // Icon ids are `CheckoutBlockIcon` values (same set TrustBadge uses), which
  // map 1:1 to Polaris `<s-icon>` types in the extension. Stored as a plain
  // string like TrustBadge's `icon`; the FE constrains the choices.
  activeIconId: z.string().optional(),
  inactiveIconId: z.string().optional(),
  // `milestones`-only label rendered above the marker (ignored by `minimal`).
  // Supports the {{amount_remaining}} token; no reached/unreached switching.
  breakpointText: z.string().optional(),
});

export type ShippingProgressTier = z.infer<typeof ShippingProgressTierSchema>;

const ShippingProgressBarCheckoutBlockVariablesSchema =
  BaseCheckoutBlockVariablesSchema.extend({
    // Header is optional for this block, unlike the shared base.
    headerText: z.string().optional(),
    // ISO 4217. Chosen on create; gates the rest of setup and drives currency
    // targeting on the experience (one currency per experience).
    currency: z.string(),
    // Persisted render discriminator (unlike trustBadge/upsell FE-only presets).
    template: z.enum(ShippingProgressBarTemplateList).default("minimal"),
    // Sorted ascending by threshold; at least one tier.
    tiers: ShippingProgressTierSchema.array().min(1),
  });

export type ShippingProgressBarBlockVariables = z.infer<
  typeof ShippingProgressBarCheckoutBlockVariablesSchema
>;

// The `#customerFirstName` banner token. Shared because the backend gate has to
// recognise the same id the admin menu inserts.
export const POST_PURCHASE_CUSTOMER_FIRST_NAME_VARIABLE_ID =
  "customerFirstName";
export const POST_PURCHASE_CUSTOMER_FIRST_NAME_TOKEN =
  `#${POST_PURCHASE_CUSTOMER_FIRST_NAME_VARIABLE_ID}` as const;

export const PostPurchaseUpsellVariablesSchema =
  BaseCheckoutBlockVariablesSchema.extend({
    discountType: z.enum(UpsellDiscountTypeList).default("none"),
    discountValue: z.number().nullish(),
    compareAtPriceDisplay: z
      .enum(UpsellCompareAtPriceDisplayList)
      .default("none"),
    imageAspectRatio: z.string(),
    excludeOutOfStock: z.boolean(),
    excludeItemsInCart: z.boolean(),
    maxProductLimit: z.number().int(),
    bannerEnabled: z.boolean(),
    bannerTitle: z.string().optional(),
    bannerSubtitle: z.string().optional(),
    customerFirstNameFallback: z.string().optional(),
    compareAtPriceEnabled: z.boolean(),
    descriptionEnabled: z.boolean(),
    quantitySelectorEnabled: z.boolean(),
    quantityLabel: z.string().optional(),
    maxQuantityType: z.string().optional(),
    maxQuantity: z.number().int().optional(),
    // Retained for older extensions that still read these settings.
    moneyLineEnabled: z.boolean().default(true),
    moneyLineSubtotal: z.boolean().default(true),
    moneyLineShipping: z.boolean().default(true),
    moneyLineTaxes: z.boolean().default(true),
    primaryButtonText: z.string().default("Accept offer"),
    secondaryButtonEnabled: z.boolean().default(true),
    secondaryButtonText: z.string().default("Decline this offer"),
    headingLevel: z.string(),
    countdownTimerEnabled: z.boolean().optional(),
    timerCheckout: TimerCheckoutBlockVariablesSchema.optional(),
    offerTimeout: z.number().optional(),
    // See `UpsellCheckoutBlockVariablesSchema.discount` — same contract.
    discount: UpsellDiscountSchema.optional(),
  });

export type PostPurchaseUpsellBlockVariables = z.infer<
  typeof PostPurchaseUpsellVariablesSchema
>;

const PostPurchaseUpsellTypographySchema = z.object({
  banner: TypographySettingsSchema.optional(),
  productTitle: TypographySettingsSchema,
  price: TypographySettingsSchema,
  description: TypographySettingsSchema.optional(),
  primaryButton: TypographySettingsSchema,
  secondaryButton: TypographySettingsSchema.optional(),
});

const PostPurchaseUpsellBackgroundColorsSchema = z.object({
  banner: z.string(),
  primaryButton: z.string(),
  secondaryButton: z.string().optional(),
  countdown: z.string().optional(),
  expired: z.string().optional(),
});

const PostPurchaseUpsellColorsSchema = z.object({
  bannerText: z.string().optional(),
  productText: z.string(),
  priceText: z.string(),
  descriptionText: z.string().optional(),
  primaryButtonText: z.string(),
  secondaryButtonText: z.string().optional(),
  expiredText: z.string().optional(),
});

const PostPurchaseUpsellAppearanceSchema = z.object({
  bannerBorderStyle: z.string(),
  bannerSpacing: z.string(),
  bannerTextAlign: z.string(),
  separatorWidth: z.string(),
  imageFit: z.string(),
  imageBorder: z.boolean(),
  pricingAppearance: z.string().optional(),
  pricingEmphasized: z.boolean(),
  pricingSubduedStyle: z.boolean().optional(),
  descriptionSubdued: z.boolean(),
  secondaryButtonStyle: z.string(),
  countdownTextAlign: z.string().optional(),
  countdownBorderStyle: z.string().optional(),
  countdownSpacing: z.string().optional(),
});

export const PostPurchaseUpsellBlockStylesSchema = z.object({
  typography: PostPurchaseUpsellTypographySchema,
  backgroundColors: PostPurchaseUpsellBackgroundColorsSchema,
  colors: PostPurchaseUpsellColorsSchema,
  appearance: PostPurchaseUpsellAppearanceSchema,
});

export type PostPurchaseUpsellBlockStyles = z.infer<
  typeof PostPurchaseUpsellBlockStylesSchema
>;

export type CheckoutBlockVariables =
  | TrustBadgeBlockVariables
  | UpsellBlockVariables
  | TimerBlockVariables
  | ShippingProgressBarBlockVariables;

export type CheckoutBlockType = Exclude<BlockType, "postPurchaseUpsell">;

export const BlockTypeList = [
  "trustBadge",
  "upsell",
  "shippingProgressBar",
  "timer",
  "postPurchaseUpsell",
] as const;
export type BlockType = (typeof BlockTypeList)[number];

// --- Block templates ---------------------------------------------------------
// A "template" is the preset a block is seeded from, recorded on every block as
// `blockTemplate`. For `shippingProgressBar` and `timer` it is also a render
// discriminator (`variables.template` / `variables.timerTemplate`); for
// `trustBadge` and `upsell` it only shapes the seeded styles and content, so
// switching one after the fact means re-applying
// the preset. The dashboard and the server-side seeder share these lists and
// the presets keyed off them (`@intelligems/ig-utils`
// `generateInitialBlockStyles` / `generateInitialBlockVariables`).

export const TrustBadgeBlockTemplateList = [
  "badgeList",
  "badgeTiles",
  "callOut",
  "grid",
  "header",
  "images",
  "brandLogo",
  "textOnly",
] as const;

export const UpsellBlockTemplateList = [
  "productTiles",
  "productList",
  "singleProduct",
] as const;

export type TrustBadgeBlockTemplateType =
  (typeof TrustBadgeBlockTemplateList)[number];

export type UpsellBadgeBlockTemplateType =
  (typeof UpsellBlockTemplateList)[number];

export const ShippingProgressBarBlockTemplateList =
  ShippingProgressBarTemplateList;
export type ShippingProgressBarBlockTemplateType = ShippingProgressBarTemplate;

export type BlockTemplateType =
  | TrustBadgeBlockTemplateType
  | UpsellBadgeBlockTemplateType
  | ShippingProgressBarBlockTemplateType
  | TimerBlockTemplateType;

/** Every template, flattened, for when the block type isn't known yet. */
export const CheckoutBlockTemplateList = [
  ...TrustBadgeBlockTemplateList,
  ...UpsellBlockTemplateList,
  ...ShippingProgressBarBlockTemplateList,
  ...TimerBlockTemplateList,
] as const;

export type BlockTemplateMap = {
  trustBadge: TrustBadgeBlockTemplateType;
  upsell: UpsellBadgeBlockTemplateType;
  shippingProgressBar: ShippingProgressBarBlockTemplateType;
  timer: TimerBlockTemplateType;
};

export type TemplateForBlockType<T extends keyof BlockTemplateMap> =
  BlockTemplateMap[T];

/** The templates each block type accepts, for validation and tool enums. */
export const CheckoutBlockTemplatesByType: {
  [K in keyof BlockTemplateMap]: readonly BlockTemplateMap[K][];
} = {
  trustBadge: TrustBadgeBlockTemplateList,
  upsell: UpsellBlockTemplateList,
  shippingProgressBar: ShippingProgressBarBlockTemplateList,
  timer: TimerBlockTemplateList,
};

/** Applied when a caller adds a block without naming a template. */
export const DefaultCheckoutBlockTemplate: {
  [K in keyof BlockTemplateMap]: BlockTemplateMap[K];
} = {
  trustBadge: "grid",
  upsell: "singleProduct",
  shippingProgressBar: "minimal",
  timer: "textCountdown",
};

export const BlockTypeName: Record<BlockType, string> = {
  trustBadge: "Trust badge",
  upsell: "Upsell",
  shippingProgressBar: "Shipping progress bar",
  timer: "Countdown timer",
  postPurchaseUpsell: "Post purchase upsell",
};

/** What to call a block built from a template — not the picker's card title,
 * since most templates present as their block type. */
export const BlockTemplateDisplayName: Record<BlockTemplateType, string> = {
  badgeList: "Trust badge",
  badgeTiles: "Trust badge",
  brandLogo: "Brand logo",
  callOut: "Call out",
  grid: "Trust badge",
  header: "Header",
  images: "Static image",
  textOnly: "Trust badge",
  productList: "Upsell",
  productTiles: "Upsell",
  singleProduct: "Upsell",
  minimal: "Shipping progress bar",
  milestones: "Shipping progress bar",
  textCountdown: "Countdown timer",
  digitalClock: "Countdown timer",
  inlinePill: "Countdown timer",
};

/** A saved block's label, falling back to its type for blocks saved before
 * templates were persisted. */
export const getCheckoutBlockDisplayName = (block: {
  blockType: BlockType;
  blockTemplate?: BlockTemplateType | null;
}): string =>
  (block.blockTemplate
    ? BlockTemplateDisplayName[block.blockTemplate]
    : undefined) ?? BlockTypeName[block.blockType];

export const BaseCheckoutBlockDtoSchema = z.object({
  id: z.uuid().describe(BaseCheckoutBlockDtoDescriptions.id),
  variationId: z.uuid().describe(BaseCheckoutBlockDtoDescriptions.variationId),
  locationId: z.string().describe(BaseCheckoutBlockDtoDescriptions.locationId),
  blockType: z
    .enum(BlockTypeList)
    .describe(BaseCheckoutBlockDtoDescriptions.blockType),
  // Never defaulted — a legacy block's template isn't recoverable, and guessing
  // one would strip settings from blocks a merchant already configured.
  blockTemplate: z
    .enum(CheckoutBlockTemplateList)
    .nullish()
    .describe(BaseCheckoutBlockDtoDescriptions.blockTemplate),
  name: z.string().describe(BaseCheckoutBlockDtoDescriptions.name),
  displayConditions: CartTargetingSchema.optional().describe(
    BaseCheckoutBlockDtoDescriptions.displayConditions
  ),
});

/** Re-attaches the base description: `.extend()` replaces the field outright,
 * so a narrowed `blockTemplate` would otherwise carry none into any parsed
 * schema. */
const narrowBlockTemplate = <T extends z.ZodType>(schema: T) =>
  schema.describe(BaseCheckoutBlockDtoDescriptions.blockTemplate);

const UpsellCheckoutBlockDtoSchema = BaseCheckoutBlockDtoSchema.extend({
  blockType: z.literal("upsell"),
  blockTemplate: narrowBlockTemplate(z.enum(UpsellBlockTemplateList).nullish()),
  variables: UpsellCheckoutBlockVariablesSchema.describe(
    UpsellCheckoutBlockDtoDescriptions.variables
  ),
  styles: UpsellBlockStylesSchema.describe(
    UpsellCheckoutBlockDtoDescriptions.styles
  ),
  upsellProductRules: UpsellProductRulesSchema.array()
    .optional()
    .default([])
    .describe(BaseCheckoutBlockDtoDescriptions.upsellProductRules),
  agenticLogicPlacementId: z
    .uuid()
    .nullish()
    .describe(BaseCheckoutBlockDtoDescriptions.agenticLogicPlacementId),
  agenticLogicRuleId: z
    .uuid()
    .nullish()
    .describe(BaseCheckoutBlockDtoDescriptions.agenticLogicRuleId),
});

const TrustBadgeCheckoutBlockDtoSchema = BaseCheckoutBlockDtoSchema.extend({
  blockType: z.literal("trustBadge"),
  blockTemplate: narrowBlockTemplate(
    z.enum(TrustBadgeBlockTemplateList).nullish()
  ),
  variables: TrustBadgeCheckoutBlockVariablesSchema.describe(
    TrustBadgeCheckoutBlockDtoDescriptions.variables
  ),
  styles: TrustBadgeBlockStylesSchema.describe(
    TrustBadgeCheckoutBlockDtoDescriptions.styles
  ),
});

const TimerCheckoutBlockDtoSchema = BaseCheckoutBlockDtoSchema.extend({
  blockType: z.literal("timer"),
  blockTemplate: narrowBlockTemplate(z.enum(TimerBlockTemplateList).nullish()),
  variables: TimerCheckoutBlockVariablesSchema,
  styles: TimerBlockStylesSchema,
});

const ShippingProgressBarCheckoutBlockDtoSchema =
  BaseCheckoutBlockDtoSchema.extend({
    blockType: z.literal("shippingProgressBar"),
    blockTemplate: narrowBlockTemplate(
      z.enum(ShippingProgressBarBlockTemplateList).nullish()
    ),
    variables: ShippingProgressBarCheckoutBlockVariablesSchema,
    styles: ShippingProgressBarBlockStylesSchema,
  });

const PostPurchaseUpsellCheckoutBlockDtoSchema =
  BaseCheckoutBlockDtoSchema.extend({
    blockType: z.literal("postPurchaseUpsell"),
    blockTemplate: narrowBlockTemplate(z.null().optional()),
    variables: PostPurchaseUpsellVariablesSchema,
    styles: PostPurchaseUpsellBlockStylesSchema,
    upsellProductRules: UpsellProductRulesSchema.array()
      .optional()
      .default([])
      .describe(BaseCheckoutBlockDtoDescriptions.upsellProductRules),
    agenticLogicPlacementId: z
      .uuid()
      .nullish()
      .describe(BaseCheckoutBlockDtoDescriptions.agenticLogicPlacementId),
    agenticLogicRuleId: z
      .uuid()
      .nullish()
      .describe(BaseCheckoutBlockDtoDescriptions.agenticLogicRuleId),
  });

export const CheckoutBlockDtoSchema = z.discriminatedUnion("blockType", [
  TrustBadgeCheckoutBlockDtoSchema,
  UpsellCheckoutBlockDtoSchema,
  ShippingProgressBarCheckoutBlockDtoSchema,
  TimerCheckoutBlockDtoSchema,
  PostPurchaseUpsellCheckoutBlockDtoSchema,
]);

export type TrustBadgeCheckoutBlockDto = z.infer<
  typeof TrustBadgeCheckoutBlockDtoSchema
>;

export type ShippingProgressBarCheckoutBlockDto = z.infer<
  typeof ShippingProgressBarCheckoutBlockDtoSchema
>;

export type UpsellCheckoutBlockDto = z.infer<
  typeof UpsellCheckoutBlockDtoSchema
>;

export type TimerCheckoutBlockDto = z.infer<typeof TimerCheckoutBlockDtoSchema>;

export type PostPurchaseUpsellCheckoutBlockDto = z.infer<
  typeof PostPurchaseUpsellCheckoutBlockDtoSchema
>;

export type CheckoutBlockDto = z.infer<typeof CheckoutBlockDtoSchema>;

export const UpsellCheckoutBlockEvaluatedDtoSchema =
  UpsellCheckoutBlockDtoSchema.extend({
    products: z.array(UpsellProductItemSchema).nullish(),
    discountType: z.enum(UpsellDiscountTypeList).optional(),
    discountValue: z.number().nullish(),
    compareAtPriceDisplay: z.enum(UpsellCompareAtPriceDisplayList).optional(),
  });

export type UpsellCheckoutBlockEvaluatedDto = z.infer<
  typeof UpsellCheckoutBlockEvaluatedDtoSchema
>;

// Union type for checkout blocks that may include evaluated upsell blocks
export const CheckoutBlockEvaluatedDtoSchema = z.discriminatedUnion(
  "blockType",
  [
    TrustBadgeCheckoutBlockDtoSchema,
    UpsellCheckoutBlockEvaluatedDtoSchema,
    ShippingProgressBarCheckoutBlockDtoSchema,
    TimerCheckoutBlockDtoSchema,
    PostPurchaseUpsellCheckoutBlockDtoSchema,
  ]
);

export type CheckoutBlockEvaluatedDto = z.infer<
  typeof CheckoutBlockEvaluatedDtoSchema
>;

// Compile-time checks: description objects must cover all schema keys
assertDescriptions<
  typeof UpsellCheckoutBlockDtoDescriptions,
  UpsellCheckoutBlockDto
>();
assertDescriptions<
  typeof TrustBadgeCheckoutBlockDtoDescriptions,
  TrustBadgeCheckoutBlockDto
>();
